import type { RequireAtLeastOne } from 'type-fest';

import { Datable } from '@/shared/types/collections';
import { SidebarOptions } from '@/shared/types/sidebar';
import type { SpeechKind, SpeechStrategy } from '@/shared/types/speechStrategy';

export type TabPath = 'apikeys' | 'models' | 'plugins';

export type ModuleType = 'credentials' | 'language_models' | 'speech_models' | 'plugins';

// Single source of truth for the sidebar panel each developer page opens — referenced
// both by the page header action and by each page's own handler (empty-state CTA),
// so the two never drift apart.
export const DEV_TOOLS_SIDEBAR_KEYS: Record<TabPath, SidebarOptions> = {
  apikeys: 'generate_api_key',
  models: 'connect_language_model',
  plugins: 'create_plugin',
};

export type CredentialType = 'use' | 'analytics' | 'manage';

export interface Credential extends Datable {
  readonly credential_id: string;
  readonly account_id: string;
  readonly created_by: string;
  readonly key: string;
  readonly type: CredentialType;
  readonly name: string;
  readonly last_used_at: string;
}

export interface Credentials {
  credentials: Credential[];
}

export interface LanguageModel extends Datable {
  readonly language_model_id: string;
  readonly account_id: string | null;
  readonly created_by: string | null;
  readonly name: string;
  readonly provider: ProviderName;
  readonly model: string;
  readonly config: Partial<{
    url: string;
    resource: string;
    deployment: string;
    max_tokens: number;
    reasoning_effort: string;
  }>;
  readonly apikey: string;
  readonly source_language_model_id?: string | null;
  enabled: boolean;
  last_used_at: string | null;
}

export type PartialLanguageModel = RequireAtLeastOne<Partial<LanguageModel>, 'language_model_id'>;

export interface LanguageModels {
  language_models: LanguageModel[];
}

export interface Model {
  name: string;
  max_tokens: number;
}

interface Provider {
  version_date?: string;
  models: Model[];
}

// `moveo` is the BYOLLM/devtools spelling; `moveo-ai` is the model-strategy
// catalog wire spelling for the same Moveo-built provider — both map to "Moveo.AI".
export type ProviderName =
  'openai' | 'azure' | 'anthropic' | 'gemini' | 'vertex_ai' | 'deepseek' | 'moveo' | 'moveo-ai';

// Sparse: the /providers BYOLLM catalog only includes connectable providers,
// not proxy-managed ones like `vertex_ai`. Consumers optional-chain accordingly.
export type Providers = Partial<Record<ProviderName, Provider>>;

export interface Plugin extends Datable {
  readonly plugin_id: string;
  readonly account_id: string;
  readonly created_by: string;
  readonly name: string;
  readonly url: string;
  readonly token: string;
  readonly events: string[];
  readonly active: boolean;
  readonly verify_ssl: boolean;
}

export interface Plugins {
  plugins: Plugin[];
}

export type SpeechModelProvider =
  'elevenlabs' | 'cartesia' | 'minimax' | 'soniox' | 'deepgram' | 'rime' | 'google';

export const ELEVENLABS_REGIONS = ['global', 'eu', 'in'] as const;

export type ElevenLabsRegion = (typeof ELEVENLABS_REGIONS)[number];

export interface SpeechModelConfig {
  api_key: string;
  region?: ElevenLabsRegion;
}

export interface SpeechModel extends Datable {
  readonly speech_model_id: string;
  readonly account_id: string;
  readonly name: string;
  readonly provider: SpeechModelProvider;
  readonly config: SpeechModelConfig;
  readonly created_by: string;
  readonly last_used_at: string | null;
}

export interface SpeechModels {
  speech_models: SpeechModel[];
}

export interface SpeechProvider {
  provider: SpeechModelProvider;
  /** Per kind, Moveo has its own key for the provider in this region. */
  hosted: Record<SpeechKind, boolean>;
  speech_model_supported: boolean;
  stt: string[];
  tts: string[];
}

export interface SpeechProviders {
  providers: SpeechProvider[];
  /** The region's recommended hosted chain per language, in the speech strategy shape. */
  defaults?: SpeechStrategy;
}

export type CreateSpeechModel = Pick<SpeechModel, 'name' | 'provider' | 'config'>;

export type UpdateSpeechModel = Pick<SpeechModel, 'speech_model_id'> & {
  name?: string;
  config?: Partial<SpeechModelConfig>;
};
